import { Alert } from "@au-schein-de/au-ui-library";
import { PRICES } from "@dransay/api/models";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useRouteLoaderData } from "react-router";
import { FULFILLMENT_GROUP_KIND } from "~/features/checkout/delivery/fulfillment-groups.resolver";
import { useCurrencyPrice } from "~/hooks/use-currency-price.hook";
import { pushCartDeliveryOptionsChangedMetric } from "~/tooling/sentry";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { deliveryMethodSelectedEvent } from "~/tooling/telemetry/events";
import type { Route } from "../../+types/root";
import { DeliveryMethodCards } from "../../components/delivery-method-cards.component";
import { VendorDetails } from "../../components/vendor-details.component";
import { useFormatDeliveryPrice } from "../../hooks/use-format-delivery-price.hook";
import { useSelectedDeliveryMethod } from "../../hooks/use-selected-delivery-method.hook";
import type { Route as StorefrontRoute } from "../../routes/+types/_storefront";
import { useCart } from "./cart.context";
import { CartPrescriptionFee } from "./cart-prescription-fee.component";

export function CartDelivery() {
	const { t } = useTranslation(["cart", "product"]);

	const {
		vendor,
		deliveryMethodUuid,
		totalPrice,
		updateDeliveryMethod: updateDeliveryMethodInCart,
	} = useCart();

	const {
		user,
		customer,
		deliveryAddress,
		enableGuestExpressCourierAvailability,
	} = useLoaderData<StorefrontRoute.ComponentProps["loaderData"]>();

	const rootLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const enableSentryCustomEvents =
		rootLoaderData?.featureFlags?.enableSentryCustomEvents ?? false;

	// Only real customers (any auth state) or guests behind the kill switch
	// get an express availability check -- mirrors the storefront loader's
	// canShowExpressCourierAvailability gate. Other authenticated roles
	// (doctor/vendor/producer/admin) can browse the storefront but must not
	// trigger this check regardless of the guest flag.
	const canCheckExpressAvailability =
		(!user || user.role === "customer") &&
		(!!user || enableGuestExpressCourierAvailability);

	const updateDeliveryMethod = useCallback(
		(deliveryMethodUuid: string) => {
			const kind = vendor?.deliveryMethods.find(
				(method) => method.uuid === deliveryMethodUuid,
			)?.kind;

			if (kind) {
				captureEvent(
					deliveryMethodSelectedEvent(kind, FULFILLMENT_GROUP_KIND.MEDICATION),
				);
				pushCartDeliveryOptionsChangedMetric(
					{
						deliveryMethodUuid,
						deliveryMethodKind: kind,
						vendorId: vendor?.id,
						vendorName: vendor?.name,
					},
					enableSentryCustomEvents,
				);
			}

			return updateDeliveryMethodInCart(deliveryMethodUuid);
		},
		[updateDeliveryMethodInCart, vendor, enableSentryCustomEvents],
	);

	if (!vendor) return null;

	return (
		<div className="flex flex-col gap-4 px-4 md:px-6 py-4">
			<span className="text-h4 text-gray-700">{t("orderingFrom")}</span>
			<VendorDetails vendor={vendor} variant="compact" />
			<DeliveryMethodCards
				vendor={vendor}
				deliveryMethodUuid={deliveryMethodUuid}
				updateDeliveryMethod={updateDeliveryMethod}
				productsPrice={totalPrice}
				showFreeDeliveryProgress
				// Guests have no DB profile address; fall back to the
				// cookie-stored header address so express availability in
				// the cart matches what they were shown in the PLP. When
				// guest access is flagged off, omit the postal code so the
				// cart never issues a /check call for unauthenticated users.
				customerPostalCode={
					canCheckExpressAvailability
						? (customer?.postalCode ?? deliveryAddress?.postalCode ?? undefined)
						: undefined
				}
			/>
		</div>
	);
}

export const CartSummary = ({ totalPrice }: { totalPrice: number }) => {
	const { t } = useTranslation("cart");
	const { vendor } = useCart();
	const { getCurrencyPrice } = useCurrencyPrice();
	const displayTotal = totalPrice + PRICES.onlineService;
	const total = getCurrencyPrice(displayTotal);

	return (
		<>
			<div className="flex flex-col gap-4 px-4 md:px-6 py-4">
				<span className="text-body-lg-emphasis text-gray-700">
					{t("orderSummary")}
				</span>
				<div className="flex flex-col gap-2">
					<div className="flex justify-between items-center">
						<p className="text-body-sm text-gray-700">
							{t("orderSummaryMedication")}
						</p>
						<span
							className="text-body-sm whitespace-nowrap text-gray-700"
							suppressHydrationWarning
						>
							{getCurrencyPrice(totalPrice)}
						</span>
					</div>
					<CartDeliveryCost />
					<CartPrescriptionFee />
					<Alert variant="success" withIcon>
						<Alert.Description>
							<p>{t("prescriptionApprovalInfo")}</p>
						</Alert.Description>
					</Alert>
				</div>
				<hr className="border-gray-200" />
				<div className="flex justify-between items-center">
					<span className="text-body text-gray-700">
						{t("orderSummaryTotal")}
					</span>
					<span
						className="text-h4 whitespace-nowrap text-gray-700"
						suppressHydrationWarning
					>
						{total}
					</span>
				</div>
			</div>
		</>
	);
};

const CartDeliveryCost = () => {
	const { deliveryMethodUuid, vendor, totalPrice } = useCart();
	const { t } = useTranslation(["cart", "common"]);

	const selectedDeliveryMethod = useSelectedDeliveryMethod({
		deliveryMethodUuid,
		deliveryMethods: vendor?.deliveryMethods ?? [],
	});

	const DeliveryCostFormatted = useFormatDeliveryPrice({
		deliveryPrice: selectedDeliveryMethod?.cost,
		deliveryMethodKind: selectedDeliveryMethod?.kind,
		totalPrice,
		deliveryMethodFreeFrom: selectedDeliveryMethod?.freeFrom,
		className: "text-body-sm text-gray-700",
	});

	return (
		<div className="flex justify-between items-center">
			<p className="text-body-sm text-gray-700">{t("shippingCost")}</p>
			<DeliveryCostFormatted />
		</div>
	);
};
